import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Grid,
    DialogActions,
    DialogContent,
    TextField,
    CircularProgress,
    Button,
    MenuItem,
    Box
} from "@mui/material";
import { useSnackbar } from "notistack";
import http, { buildFormData } from "src/utils/httpHelper";
import InvoiceExpeditor from "src/models/invoiceExpeditor";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import NativeSelect from '@mui/material/NativeSelect';
import InputLabel from '@mui/material/InputLabel';

interface InvoiceExpeditorFormProps {
    modalCloseCallback,
    onSuccessCallback
    item?: InvoiceExpeditor
}

const InvoiceExpeditorForm = ({ modalCloseCallback, onSuccessCallback, item }: InvoiceExpeditorFormProps) => {
    const { t }: { t: any } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const [logoSelect, setLogoSelect] = useState(0);

    const { handleSubmit, formState: { errors, isSubmitting, isValid }, control, setValue, reset, trigger } = useForm({
        mode: 'onTouched',
        resolver: yupResolver(InvoiceExpeditor.getRules(item)),
        defaultValues: InvoiceExpeditor.getInitValues(item)
    });

    return (
        <form noValidate onSubmit={handleSubmit(async (values) => {
            const formData = new FormData();
            buildFormData(formData, {...values});
            let inputFile = (document.getElementById('logo') as HTMLInputElement).files[0];
            if (inputFile) {
                formData.set("logo", inputFile);
            } else {
                formData.delete("logo");
            }
            await http.request({
                method: 'post',
                url: item ? `/api/invoice-expeditors/${item.id}` : '/api/invoice-expeditors',
                data: formData
            })
                .then((response) => {
                    onSuccessCallback();
                    enqueueSnackbar(response.data.message, { variant: 'success' });
                })
                .catch((error) => {
                    console.error(error);
                    enqueueSnackbar(error.message, { variant: 'error' });
                });
        })}>
            <DialogContent
                dividers
                sx={{
                    p: 3,
                }}
            >
                <Grid container spacing={3}>
                    <Grid item xs={12} md={12}>
                        <Controller
                            name="rfc"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    error={Boolean(errors.rfc)}
                                    fullWidth
                                    helperText={errors.rfc?.message}
                                    label={t("FIELDS.RFC")}
                                    variant="outlined"
                                    {...field}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Controller
                            name="name"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    error={Boolean(errors.name)}
                                    fullWidth
                                    helperText={errors.name?.message}
                                    label={t("FIELDS.NAME")}
                                    variant="outlined"
                                    {...field}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Controller
                            name="postal_code"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    error={Boolean(errors.postal_code)}
                                    fullWidth
                                    helperText={errors.postal_code?.message}
                                    label={t("FIELDS.POSTAL_CODE")}
                                    variant="outlined"
                                    {...field}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Controller
                            name="tax_regime"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    error={Boolean(errors.tax_regime)}
                                    fullWidth
                                    helperText={errors.tax_regime?.message}
                                    label={t("FIELDS.TAX_REGIME")}
                                    variant="outlined"
                                    {...field}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Controller
                            name="street"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    error={Boolean(errors.street)}
                                    fullWidth
                                    helperText={errors.street?.message}
                                    label={t("FIELDS.STREET")}
                                    variant="outlined"
                                    {...field}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Controller
                            name="suburb"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    error={Boolean(errors.suburb)}
                                    fullWidth
                                    helperText={errors.suburb?.message}
                                    label={t("FIELDS.SUBURB")}
                                    variant="outlined"
                                    {...field}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Controller
                            name="city"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    error={Boolean(errors.city)}
                                    fullWidth
                                    helperText={errors.city?.message}
                                    label={t("FIELDS.CITY")}
                                    variant="outlined"
                                    {...field}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Controller
                            name="state"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    error={Boolean(errors.state)}
                                    fullWidth
                                    helperText={errors.state?.message}
                                    label={t("FIELDS.STATE")}
                                    variant="outlined"
                                    {...field}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Controller
                            name="country"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    error={Boolean(errors.country)}
                                    fullWidth
                                    helperText={errors.country?.message}
                                    label={t("FIELDS.COUNTRY")}
                                    variant="outlined"
                                    {...field}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Controller
                            name="expedition_place"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    error={Boolean(errors.expedition_place)}
                                    fullWidth
                                    helperText={errors.expedition_place?.message}
                                    label={t("FIELDS.EXPEDITION_PLACE")}
                                    variant="outlined"
                                    {...field}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <InputLabel htmlFor="logo">{t("FIELDS.LOGO")}</InputLabel>
                        <Controller
                            name="logo"
                            control={control}
                            render={({ field }) => (
                                <input
                                    accept=".jpg, .jpeg"
                                    id="logo"
                                    type="file"
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Controller
                            name="logo_type"
                            control={control}
                            defaultValue={item?item.logo_type:0}
                            render={({ field }) => (
                                <TextField
                                    select
                                    sx={{ pt: 1 }}
                                    error={Boolean(errors.logo_type)}
                                    fullWidth
                                    helperText={errors.logo_type?.message}
                                    label={t("FIELDS.LOGO_TYPE")}
                                    variant="outlined"
                                    value={logoSelect}
                                    id="logo_type"
                                    onChange={(event) => {
                                        setLogoSelect(parseInt(event.target.value));
                                    }}
                                    {...field}
                                >
                                    <MenuItem value={0}>Logo pequeño</MenuItem>
                                    <MenuItem value={1}>Logo ancho</MenuItem>
                                </TextField>
                            )}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions
                sx={{
                    p: 3,
                    display: 'flex',
                    justifyContent: 'flex-end'
                }}
            >
                <Box>
                    <Button color="secondary" onClick={modalCloseCallback}>
                        {t("GENERAL.CANCEL")}
                    </Button>
                    <Button
                        sx={{
                            ml: 2
                        }}
                        type="submit"
                        startIcon={
                            isSubmitting ? <CircularProgress size="1rem" /> : null
                        }
                        disabled={isSubmitting || !isValid}
                        variant="contained"
                    >
                        {t(!item ? "INVOICE-EXPEDITORS.CREATE" : 'INVOICE-EXPEDITORS.EDIT')}
                    </Button>
                </Box>
            </DialogActions>
        </form>
    )
}

export default InvoiceExpeditorForm;